"use client"

import { useState, useEffect } from "react"
import { ProjectType, ProjectConfig } from "./ProjectConfigurator"
import { QuestionCard } from "./QuestionCard"
import { Button } from "@/components/ui/button"
import { Typography } from "@/components/typography"
import { Textarea } from "@/components/ui/textarea"

interface ConditionalQuestionsProps {
  projectTypes: ProjectType[]
  config: ProjectConfig
  onUpdate: (updates: Partial<ProjectConfig>) => void
  onNext: () => void
  onBack: () => void
}

export function ConditionalQuestions({
  projectTypes,
  config,
  onUpdate,
  onNext,
  onBack,
}: ConditionalQuestionsProps) {
  const [currentTypeIndex, setCurrentTypeIndex] = useState(0)
  
  // Reset index if it's out of bounds when projectTypes changes
  useEffect(() => {
    setCurrentTypeIndex((prevIndex) => {
      if (projectTypes.length === 0) {
        return 0
      }
      // If current index is out of bounds, reset to 0
      if (prevIndex >= projectTypes.length) {
        return 0
      }
      // Otherwise keep the current index
      return prevIndex
    })
  }, [projectTypes.length]) // Only depend on length to avoid unnecessary resets
  
  // Ensure we have a valid currentType
  const currentType = projectTypes.length > 0 ? projectTypes[Math.min(currentTypeIndex, projectTypes.length - 1)] : undefined

  const handleNextType = () => {
    if (projectTypes.length === 0) {
      onBack() // Go back if no types selected
      return
    }
    
    const safeIndex = Math.min(currentTypeIndex, projectTypes.length - 1)
    if (safeIndex < projectTypes.length - 1) {
      setCurrentTypeIndex(safeIndex + 1)
    } else {
      onNext()
    }
  }

  const handleBackType = () => {
    if (projectTypes.length === 0) {
      onBack() // Go back if no types selected
      return
    }
    
    const safeIndex = Math.min(currentTypeIndex, projectTypes.length - 1)
    if (safeIndex > 0) {
      setCurrentTypeIndex(safeIndex - 1)
    } else {
      onBack()
    }
  }

  const renderWebsiteQuestions = () => {
    const website = config.website || {}

    return (
      <div className="space-y-8">
        {/* Website Type */}
        <div>
          <Typography variant="h3" as="h3" className="mb-4">
            What type of website?
          </Typography>
          <div className="grid gap-3 sm:grid-cols-2">
            {["Custom (Next.js/React)", "WordPress", "Headless WordPress", "Not sure"].map(
              (option) => (
                <QuestionCard
                  key={option}
                  label={option}
                  selected={website.type === option}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    onUpdate({ website: { ...(config.website || {}), type: option } })
                  }}
                />
              )
            )}
          </div>
        </div>

        {/* Current Situation */}
        <div>
          <Typography variant="h3" as="h3" className="mb-4">
            Current situation?
          </Typography>
          <div className="grid gap-3 sm:grid-cols-2">
            {[
              "New site",
              "Redesign existing",
              "Migrate from another platform",
              "Optimize existing",
            ].map((option) => (
              <QuestionCard
                key={option}
                label={option}
                selected={website.currentSituation === option}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  onUpdate({ website: { ...(config.website || {}), currentSituation: option } })
                }}
              />
            ))}
          </div>
        </div>

        {/* Design */}
        <div>
          <Typography variant="h3" as="h3" className="mb-4">
            Design needs?
          </Typography>
          <div className="grid gap-3 sm:grid-cols-2">
            {["Have designer", "Need full design", "Have partial design"].map((option) => (
              <QuestionCard
                key={option}
                label={option}
                selected={website.design === option}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  onUpdate({ website: { ...(config.website || {}), design: option } })
                }}
              />
            ))}
          </div>
        </div>

        {/* Pain Points */}
        <div>
          <Typography variant="h3" as="h3" className="mb-4">
            What&apos;s not working? (Select all that apply)
          </Typography>
          <div className="grid gap-3 sm:grid-cols-2">
            {[
              "Slow performance",
              "Hard to update",
              "Doesn't convert",
              "Outdated",
              "Broken features",
              "Other",
            ].map((option) => {
              const isSelected = website.painPoints?.includes(option) || false
              return (
                <QuestionCard
                  key={option}
                  label={option}
                  selected={isSelected}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    const currentWebsite = config.website || {}
                    const current = currentWebsite.painPoints || []
                    const updated = isSelected
                      ? current.filter((p) => p !== option)
                      : [...current, option]
                    onUpdate({ website: { ...currentWebsite, painPoints: updated } })
                  }}
                />
              )
            })}
          </div>
        </div>

        {/* Features */}
        <div>
          <Typography variant="h3" as="h3" className="mb-4">
            Features needed? (Select all that apply)
          </Typography>
          <div className="grid gap-3 sm:grid-cols-2">
            {["E-commerce", "CMS", "Integrations", "Member portal", "Other"].map((option) => {
              const isSelected = website.features?.includes(option) || false
              return (
                <QuestionCard
                  key={option}
                  label={option}
                  selected={isSelected}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    const currentWebsite = config.website || {}
                    const current = currentWebsite.features || []
                    const updated = isSelected
                      ? current.filter((f) => f !== option)
                      : [...current, option]
                    onUpdate({ website: { ...currentWebsite, features: updated } })
                  }}
                />
              )
            })}
          </div>
        </div>
      </div>
    )
  }

  const renderAppQuestions = () => {
    const app = config.app || {}

    return (
      <div className="space-y-8">
        <div>
          <Typography variant="h3" as="h3" className="mb-4">
            What type of app?
          </Typography>
          <div className="grid gap-3 sm:grid-cols-2">
            {["Web app", "Dashboard", "Internal tool", "Public-facing app"].map((option) => (
              <QuestionCard
                key={option}
                label={option}
                selected={app.type === option}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  const currentApp = config.app || {}
                  onUpdate({ app: { ...currentApp, type: option } })
                }}
              />
            ))}
          </div>
        </div>

        <div>
          <Typography variant="h3" as="h3" className="mb-4">
            Who will use it?
          </Typography>
          <div className="grid gap-3 sm:grid-cols-2">
            {["Internal team", "Customers", "Both"].map((option) => (
              <QuestionCard
                key={option}
                label={option}
                selected={app.users === option}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  const currentApp = config.app || {}
                  onUpdate({ app: { ...currentApp, users: option } })
                }}
              />
            ))}
          </div>
        </div>

        <div>
          <Typography variant="h3" as="h3" className="mb-4">
            What does it need to do?
          </Typography>
          <Textarea
            placeholder="Brief description of the app's purpose..."
            value={app.purpose || ""}
            onChange={(e) => {
              const currentApp = config.app || {}
              onUpdate({ app: { ...currentApp, purpose: e.target.value } })
            }}
            className="min-h-[100px] border-tk-slate/20 bg-tk-linen"
          />
        </div>

        <div>
          <Typography variant="h3" as="h3" className="mb-4">
            Pain points? (Select all that apply)
          </Typography>
          <div className="grid gap-3 sm:grid-cols-2">
            {[
              "Manual processes",
              "Data scattered",
              "No visibility",
              "Inefficient workflows",
            ].map((option) => {
              const isSelected = app.painPoints?.includes(option) || false
              return (
                <QuestionCard
                  key={option}
                  label={option}
                  selected={isSelected}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    const currentApp = config.app || {}
                    const current = currentApp.painPoints || []
                    const updated = isSelected
                      ? current.filter((p) => p !== option)
                      : [...current, option]
                    onUpdate({ app: { ...currentApp, painPoints: updated } })
                  }}
                />
              )
            })}
          </div>
        </div>
      </div>
    )
  }

  const renderToolQuestions = () => {
    const tool = config.tool || {}

    return (
      <div className="space-y-8">
        <div>
          <Typography variant="h3" as="h3" className="mb-4">
            What type of tool?
          </Typography>
          <div className="grid gap-3 sm:grid-cols-2">
            {["Dashboard", "Reporting system", "Automation tool", "Data processing"].map(
              (option) => (
                <QuestionCard
                  key={option}
                  label={option}
                  selected={tool.type === option}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    const currentTool = config.tool || {}
                    onUpdate({ tool: { ...currentTool, type: option } })
                  }}
                />
              )
            )}
          </div>
        </div>

        <div>
          <Typography variant="h3" as="h3" className="mb-4">
            Pain points? (Select all that apply)
          </Typography>
          <div className="grid gap-3 sm:grid-cols-2">
            {["Manual work", "Disconnected data", "No real-time info", "Can't scale"].map(
              (option) => {
                const isSelected = tool.painPoints?.includes(option) || false
                return (
                  <QuestionCard
                    key={option}
                    label={option}
                    selected={isSelected}
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      const currentTool = config.tool || {}
                      const current = currentTool.painPoints || []
                      const updated = isSelected
                        ? current.filter((p) => p !== option)
                        : [...current, option]
                      onUpdate({ tool: { ...currentTool, painPoints: updated } })
                    }}
                  />
                )
              }
            )}
          </div>
        </div>
      </div>
    )
  }

  const renderDesignQuestions = () => {
    const design = config.design || {}

    return (
      <div className="space-y-8">
        <div>
          <Typography variant="h3" as="h3" className="mb-4">
            What type of design work?
          </Typography>
          <div className="grid gap-3 sm:grid-cols-2">
            {[
              "Full rebrand",
              "Identity refinement",
              "Messaging/positioning",
              "Visual systems",
              "Brand audit",
            ].map((option) => (
              <QuestionCard
                key={option}
                label={option}
                selected={design.type === option}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  const currentDesign = config.design || {}
                  onUpdate({ design: { ...currentDesign, type: option } })
                }}
              />
            ))}
          </div>
        </div>

        <div>
          <Typography variant="h3" as="h3" className="mb-4">
            Current brand state?
          </Typography>
          <div className="grid gap-3 sm:grid-cols-2">
            {["Have brand", "Need brand", "Need refresh"].map((option) => (
              <QuestionCard
                key={option}
                label={option}
                selected={design.currentState === option}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  const currentDesign = config.design || {}
                  onUpdate({ design: { ...currentDesign, currentState: option } })
                }}
              />
            ))}
          </div>
        </div>

        <div>
          <Typography variant="h3" as="h3" className="mb-4">
            Pain points? (Select all that apply)
          </Typography>
          <div className="grid gap-3 sm:grid-cols-2">
            {["Inconsistent", "Outdated", "Unclear messaging", "Doesn't reflect business"].map(
              (option) => {
                const isSelected = design.painPoints?.includes(option) || false
                return (
                  <QuestionCard
                    key={option}
                    label={option}
                    selected={isSelected}
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      const currentDesign = config.design || {}
                      const current = currentDesign.painPoints || []
                      const updated = isSelected
                        ? current.filter((p) => p !== option)
                        : [...current, option]
                      onUpdate({ design: { ...currentDesign, painPoints: updated } })
                    }}
                  />
                )
              }
            )}
          </div>
        </div>
      </div>
    )
  }

  const renderIntegrationsQuestions = () => {
    const integrations = config.integrations || {}

    return (
      <div className="space-y-8">
        <div>
          <Typography variant="h3" as="h3" className="mb-4">
            Systems to connect? (Select all that apply)
          </Typography>
          <div className="grid gap-3 sm:grid-cols-2">
            {[
              "CRM (Salesforce)",
              "CRM (HubSpot)",
              "CRM (Pipedrive)",
              "CRM (Other)",
              "Marketing tools",
              "E-commerce",
              "Internal tools",
              "Other",
            ].map((option) => {
              const isSelected = integrations.systems?.includes(option) || false
              return (
                <QuestionCard
                  key={option}
                  label={option}
                  selected={isSelected}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    const currentIntegrations = config.integrations || {}
                    const current = currentIntegrations.systems || []
                    const updated = isSelected
                      ? current.filter((s) => s !== option)
                      : [...current, option]
                    onUpdate({ integrations: { ...currentIntegrations, systems: updated } })
                  }}
                />
              )
            })}
          </div>
        </div>

        <div>
          <Typography variant="h3" as="h3" className="mb-4">
            Pain points? (Select all that apply)
          </Typography>
          <div className="grid gap-3 sm:grid-cols-2">
            {[
              "Manual data entry",
              "Duplicate work",
              "Broken workflows",
              "No visibility",
            ].map((option) => {
              const isSelected = integrations.painPoints?.includes(option) || false
              return (
                <QuestionCard
                  key={option}
                  label={option}
                  selected={isSelected}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    const currentIntegrations = config.integrations || {}
                    const current = currentIntegrations.painPoints || []
                    const updated = isSelected
                      ? current.filter((p) => p !== option)
                      : [...current, option]
                    onUpdate({ integrations: { ...currentIntegrations, painPoints: updated } })
                  }}
                />
              )
            })}
          </div>
        </div>

        <div>
          <Typography variant="h3" as="h3" className="mb-4">
            Main goal?
          </Typography>
          <div className="grid gap-3 sm:grid-cols-2">
            {["Automate workflows", "Sync data", "Build pipelines", "Other"].map((option) => (
              <QuestionCard
                key={option}
                label={option}
                selected={integrations.goal === option}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  const currentIntegrations = config.integrations || {}
                  onUpdate({ integrations: { ...currentIntegrations, goal: option } })
                }}
              />
            ))}
          </div>
        </div>
      </div>
    )
  }

  const renderConsultingQuestions = () => {
    const consulting = config.consulting || {}

    return (
      <div className="space-y-8">
        <div>
          <Typography variant="h3" as="h3" className="mb-4">
            What type of consulting?
          </Typography>
          <div className="grid gap-3 sm:grid-cols-2">
            {[
              "Systems strategy",
              "Architecture design",
              "Requirements translation",
              "Technology roadmap",
              "Implementation guidance",
            ].map((option) => (
              <QuestionCard
                key={option}
                label={option}
                selected={consulting.type === option}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  const currentConsulting = config.consulting || {}
                  onUpdate({ consulting: { ...currentConsulting, type: option } })
                }}
              />
            ))}
          </div>
        </div>

        <div>
          <Typography variant="h3" as="h3" className="mb-4">
            Current situation?
          </Typography>
          <div className="grid gap-3 sm:grid-cols-2">
            {[
              "Planning new systems",
              "Optimizing existing",
              "Need technical guidance",
              "Bridging teams",
            ].map((option) => (
              <QuestionCard
                key={option}
                label={option}
                selected={consulting.currentSituation === option}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  const currentConsulting = config.consulting || {}
                  onUpdate({ consulting: { ...currentConsulting, currentSituation: option } })
                }}
              />
            ))}
          </div>
        </div>

        <div>
          <Typography variant="h3" as="h3" className="mb-4">
            Pain points? (Select all that apply)
          </Typography>
          <div className="grid gap-3 sm:grid-cols-2">
            {[
              "No clear strategy",
              "Business-tech disconnect",
              "Systems don't align",
              "Need architecture guidance",
            ].map((option) => {
              const isSelected = consulting.painPoints?.includes(option) || false
              return (
                <QuestionCard
                  key={option}
                  label={option}
                  selected={isSelected}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    const currentConsulting = config.consulting || {}
                    const current = currentConsulting.painPoints || []
                    const updated = isSelected
                      ? current.filter((p) => p !== option)
                      : [...current, option]
                    onUpdate({ consulting: { ...currentConsulting, painPoints: updated } })
                  }}
                />
              )
            })}
          </div>
        </div>

        <div>
          <Typography variant="h3" as="h3" className="mb-4">
            Main goals? (Select all that apply)
          </Typography>
          <div className="grid gap-3 sm:grid-cols-2">
            {[
              "Systems architecture",
              "Technology strategy",
              "Requirements translation",
              "Team alignment",
            ].map((option) => {
              const isSelected = consulting.goals?.includes(option) || false
              return (
                <QuestionCard
                  key={option}
                  label={option}
                  selected={isSelected}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    const currentConsulting = config.consulting || {}
                    const current = currentConsulting.goals || []
                    const updated = isSelected
                      ? current.filter((g) => g !== option)
                      : [...current, option]
                    onUpdate({ consulting: { ...currentConsulting, goals: updated } })
                  }}
                />
              )
            })}
          </div>
        </div>
      </div>
    )
  }

  const renderUnknownQuestions = () => {
    const unknown = config.unknown || {}

    return (
      <div className="space-y-8">
        <div>
          <Typography variant="h3" as="h3" className="mb-4">
            What&apos;s not working?
          </Typography>
          <Textarea
            placeholder="Describe what's not working in your current setup..."
            value={unknown.whatsNotWorking || ""}
            onChange={(e) => {
              const currentUnknown = config.unknown || {}
              onUpdate({ unknown: { ...currentUnknown, whatsNotWorking: e.target.value } })
            }}
            className="min-h-[100px] border-tk-slate/20 bg-tk-linen"
          />
        </div>

        <div>
          <Typography variant="h3" as="h3" className="mb-4">
            What are you trying to achieve?
          </Typography>
          <Textarea
            placeholder="What's your main goal or desired outcome?"
            value={unknown.tryingToAchieve || ""}
            onChange={(e) => {
              const currentUnknown = config.unknown || {}
              onUpdate({ unknown: { ...currentUnknown, tryingToAchieve: e.target.value } })
            }}
            className="min-h-[100px] border-tk-slate/20 bg-tk-linen"
          />
        </div>

        <div>
          <Typography variant="h3" as="h3" className="mb-4">
            Common pain points? (Select all that apply)
          </Typography>
          <div className="grid gap-3 sm:grid-cols-2">
            {[
              "Manual processes",
              "Disconnected systems",
              "Outdated tools",
              "No clear direction",
              "Everything feels broken",
            ].map((option) => {
              const isSelected = unknown.painPoints?.includes(option) || false
              return (
                <QuestionCard
                  key={option}
                  label={option}
                  selected={isSelected}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    const currentUnknown = config.unknown || {}
                    const current = currentUnknown.painPoints || []
                    const updated = isSelected
                      ? current.filter((p) => p !== option)
                      : [...current, option]
                    onUpdate({ unknown: { ...currentUnknown, painPoints: updated } })
                  }}
                />
              )
            })}
          </div>
        </div>
      </div>
    )
  }

  const getTypeLabel = (type: ProjectType | undefined): string => {
    if (!type) return "project"
    const labels: Record<ProjectType, string> = {
      website: "Website",
      app: "App",
      tool: "Tool",
      design: "Design Work",
      integrations: "Integrations",
      consulting: "Business Consulting",
      unknown: "I Don't Know",
    }
    return labels[type] || "project"
  }

  const renderQuestions = () => {
    if (!currentType) {
      return (
        <div className="p-4 rounded-lg bg-tk-teal/10 border border-tk-teal/20">
          <Typography variant="body" className="text-tk-teal font-semibold">
            No project types selected. Please go back and select at least one project type.
          </Typography>
        </div>
      )
    }
    
    switch (currentType) {
      case "website":
        return renderWebsiteQuestions()
      case "app":
        return renderAppQuestions()
      case "tool":
        return renderToolQuestions()
      case "design":
        return renderDesignQuestions()
      case "integrations":
        return renderIntegrationsQuestions()
      case "consulting":
        return renderConsultingQuestions()
      case "unknown":
        return renderUnknownQuestions()
      default:
        return (
          <div className="p-4 rounded-lg bg-tk-teal/10 border border-tk-teal/20">
            <Typography variant="body" className="text-tk-teal font-semibold">
              Unknown project type. Please go back and select a valid project type.
            </Typography>
          </div>
        )
    }
  }

  // Early return if no project types
  if (!projectTypes || projectTypes.length === 0) {
    return (
      <div className="space-y-8">
        <div className="p-4 rounded-lg bg-tk-teal/10 border border-tk-teal/20">
          <Typography variant="body" className="text-tk-teal font-semibold">
            No project types selected. Please go back and select at least one project type.
          </Typography>
        </div>
        <div className="flex justify-end pt-6">
          <Button
            onClick={onBack}
            variant="outline"
            className="rounded-full border-tk-slate/25 bg-tk-linen text-sm text-tk-slate hover:border-tk-teal hover:text-tk-onyx font-ui"
          >
            ← Back
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <div>
        <Typography variant="h2" as="h2" className="mb-2">
          Tell me about your {currentType ? getTypeLabel(currentType).toLowerCase() : "project"}
        </Typography>
        {projectTypes.length > 1 && (
          <Typography variant="body-sm" className="text-tk-slate/70">
            {Math.min(currentTypeIndex + 1, projectTypes.length)} of {projectTypes.length} project types
          </Typography>
        )}
      </div>

      {renderQuestions()}

      <div className="flex justify-between pt-6">
        <Button
          onClick={handleBackType}
          variant="outline"
          className="rounded-full border-tk-slate/25 bg-tk-linen text-sm text-tk-slate hover:border-tk-teal hover:text-tk-onyx font-ui"
        >
          ← Back
        </Button>
        <Button
          onClick={handleNextType}
          disabled={projectTypes.length === 0}
          className="rounded-full bg-tk-teal px-8 py-2 text-sm font-semibold text-tk-linen hover:bg-tk-teal/90 font-ui disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {projectTypes.length > 0 && Math.min(currentTypeIndex, projectTypes.length - 1) < projectTypes.length - 1 
            ? "Next →" 
            : "Continue →"}
        </Button>
      </div>
    </div>
  )
}

